// Copyright 2016 Google Inc.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

interface SyncManager {
  getTags(): Promise<string[]>;
  register(tag: string): Promise<void>;
}

declare global {
  interface ServiceWorkerRegistration {
    readonly sync: SyncManager;
  }

  interface SyncEvent extends ExtendableEvent {
    readonly lastChance: boolean;
    readonly tag: string;
  }

  interface ServiceWorkerGlobalScopeEventMap {
    sync: SyncEvent;
  }
}

export type PermissionName =
  | 'geolocation'
  | 'notifications'
  | 'push'
  | 'midi'
  | 'camera'
  | 'microphone'
  | 'speaker'
  | 'device-info'
  | 'background-sync'
  | 'bluetooth'
  | 'persistent-storage';

export interface PermissionDescriptor {
  name: PermissionName;
}

export interface GeolocationPermissionDescriptor extends PermissionDescriptor {
  name: 'geolocation';
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

interface NotificationsPermissionDescriptor extends PermissionDescriptor {
  name: 'notifications';
}

export interface PushPermissionDescriptor extends PermissionDescriptor {
  name: 'push';
  serviceWorker: ServiceWorkerRegistration;
  userVisibleOnly?: boolean;
  applicationServerKey?: ArrayBuffer | ArrayBufferView;
}

export interface MidiPermissionDescriptor extends PermissionDescriptor {
  name: 'midi';
  sysex: boolean;
  software: boolean;
}

// Use DOM's built-in MediaTrackConstraints; keep compatibility for environments
// where only MediaStreamConstraints are available.
type TrackConstraints = MediaTrackConstraints | MediaStreamConstraints;

export interface CameraPermissionDescriptor extends PermissionDescriptor {
  name: 'camera';
  constraints?: TrackConstraints;
  peerIdentity?: string;
}

export interface MicrophonePermissionDescriptor extends PermissionDescriptor {
  name: 'microphone';
  constraints?: TrackConstraints;
  peerIdentity?: string;
}

interface SpeakerPermissionDescriptor extends PermissionDescriptor {
  name: 'speaker';
}

interface DeviceInfoPermissionDescriptor extends PermissionDescriptor {
  name: 'device-info';
}

export interface BackgroundSyncPermissionDescriptor
  extends PermissionDescriptor {
  name: 'background-sync';
  serviceWorker: ServiceWorkerRegistration;
  tag: string;
}

export interface BluetoothPermissionDescriptor extends PermissionDescriptor {
  name: 'bluetooth';
  deviceId?: string;
  // These match RequestDeviceOptions.
  filters?: any[];
  optionalServices?: any[];
}

interface PersistentStoragePermissionDescriptor extends PermissionDescriptor {
  name: 'persistent-storage';
}

export enum PermissionStateEnum {
  GRANTED = 'granted',
  DENIED = 'denied',
  PROMPT = 'prompt',
}

export type PermissionState = PermissionStateEnum;

// Query and Request results:

export interface PermissionStatus {
  state: PermissionState;
}

interface Coordinates {
  longitude: number;
  latitude: number;
}

export interface GeolocationPermissionResult extends PermissionStatus {
  // For stream=false, from Position:
  coords?: Coordinates;
  timestamp?: number;
}

interface NotificationsPermissionResult extends PermissionStatus {}

export interface PushPermissionResult extends PermissionStatus {
  subscription: PushSubscription | null;
}

type MIDIAccess = any;

export interface MidiPermissionResult extends PermissionStatus {
  access: MIDIAccess;
}

type MediaStream = any;

export interface CameraPermissionResult extends PermissionStatus {
  stream?: MediaStream;
}

export interface MicrophonePermissionResult extends PermissionStatus {
  stream?: MediaStream;
}

interface SpeakerPermissionResult extends PermissionStatus {}

interface DeviceInfoPermissionResult extends PermissionStatus {}

interface BackgroundSyncPermissionResult extends PermissionStatus {}

type BluetoothDevice = any;

export interface BluetoothPermissionResult extends PermissionStatus {
  devices: BluetoothDevice[];
}

interface PersistentStoragePermissionResult extends PermissionStatus {}

export interface Permissions {
  query(permissionDesc: PermissionDescriptor): Promise<PermissionStatus>;

  request(
    permissionDesc: GeolocationPermissionDescriptor,
  ): Promise<PermissionStatus>;

  request(
    permissionDesc: NotificationsPermissionDescriptor,
  ): Promise<PermissionStatus>;

  request(permissionDesc: PushPermissionDescriptor): Promise<PermissionStatus>;

  request(permissionDesc: MidiPermissionDescriptor): Promise<PermissionStatus>;

  request(
    permissionDesc: CameraPermissionDescriptor,
  ): Promise<PermissionStatus>;

  request(
    permissionDesc: MicrophonePermissionDescriptor,
  ): Promise<PermissionStatus>;

  request(
    permissionDesc: SpeakerPermissionDescriptor,
  ): Promise<PermissionStatus>;

  request(
    permissionDesc: DeviceInfoPermissionDescriptor,
  ): Promise<PermissionStatus>;

  request(
    permissionDesc: BackgroundSyncPermissionDescriptor,
  ): Promise<PermissionStatus>;

  request(
    permissionDesc: BluetoothPermissionDescriptor,
  ): Promise<BluetoothPermissionResult>;

  request(
    permissionDesc: PersistentStoragePermissionDescriptor,
  ): Promise<PermissionStatus>;
}

declare global {
  interface Navigator {
    // @ts-ignore
    permissions: Permissions;

    // Incomplete members from other specs:
    bluetooth: any;
    // @ts-ignore
    mediaDevices: {
      getUserMedia(
        opts: any,
        success: (stream: MediaStream) => any,
        failure: (err: DOMException) => any,
      ): void;
    };

    requestMIDIAccess(options?: any): Promise<MIDIAccess>;

    storage: { persist(): Promise<boolean> };
  }

  // const ServiceWorkerRegistration: {
  //   prototype: ServiceWorkerRegistration;
  //   new(): ServiceWorkerRegistration;
  // };
}
declare const Notification: {
  requestPermission(): Promise<'default' | 'denied' | 'granted'>;
};

function tryQuery(
  permissionDesc: PermissionDescriptor,
  dflt: PermissionState,
): Promise<PermissionStatus> {
  if (navigator.permissions && navigator.permissions.query) {
    // @ts-ignore
    return navigator.permissions.query(permissionDesc);
  }
  return Promise.resolve({ state: dflt });
}

function queryGeolocation() {
  try {
    const savedStatus = window.localStorage.getItem(
      'geolocationPermission',
    ) as PermissionStateEnum;
    return Promise.resolve({
      state: savedStatus
        ? savedStatus
        : navigator?.geolocation
          ? PermissionStateEnum.PROMPT
          : PermissionStateEnum.DENIED,
    });
  } catch (e) {
    return Promise.resolve({
      state: navigator?.geolocation
        ? PermissionStateEnum.PROMPT
        : PermissionStateEnum.DENIED,
    });
  }
}

type GeoErrorLike = { code?: number } | null;

export function keepLocationPermission(error: GeoErrorLike = null) {
  const permissionState = error
    ? error.code === 1
      ? PermissionStateEnum.DENIED
      : PermissionStateEnum.PROMPT
    : PermissionStateEnum.GRANTED;

  try {
    window.localStorage.setItem('geolocationPermission', permissionState);
  } catch (e) {}
}

function requestGeolocation(
  permissionDesc: GeolocationPermissionDescriptor,
): Promise<GeolocationPermissionResult> {
  return new Promise((resolve) => {
    const { enableHighAccuracy, timeout, maximumAge } = permissionDesc;
    return navigator?.geolocation?.getCurrentPosition(
      ({ coords, timestamp }) => {
        return resolve(
          tryQuery(permissionDesc, PermissionStateEnum.GRANTED).then(
            ({ state }) => {
              return { state, coords, timestamp };
            },
          ),
        );
      },
      () => {
        return resolve(tryQuery(permissionDesc, PermissionStateEnum.PROMPT));
      },
      { enableHighAccuracy, timeout, maximumAge },
    );
  });
}

function requestNotifications(): Promise<NotificationsPermissionResult> {
  return Notification.requestPermission().then((state) => {
    switch (state) {
      case 'default':
        return { state: PermissionStateEnum.PROMPT };
      case 'granted':
        return { state: PermissionStateEnum.GRANTED };
      case 'denied':
        return { state: PermissionStateEnum.DENIED };
    }
    throw new TypeError(state);
  });
}

function requestPush(
  permissionDesc: PushPermissionDescriptor,
): Promise<PushPermissionResult> {
  return new Promise((resolve) => {
    const { serviceWorker, userVisibleOnly, applicationServerKey } =
      permissionDesc as PushPermissionDescriptor;
    if (!(serviceWorker instanceof ServiceWorkerRegistration)) {
      throw new TypeError();
    }
    resolve(
      serviceWorker.pushManager
        .subscribe({ userVisibleOnly, applicationServerKey })
        .then(
          (subscription) => {
            return { subscription, state: PermissionStateEnum.GRANTED };
          },
          (err) => {
            if (err.name === 'NotAllowedError') {
              return { state: PermissionStateEnum.DENIED, subscription: null };
            }
            // Re-throw SecurityError, InvalidStateError,
            // and AbortError.
            throw err;
          },
        ),
    );
  });
}

function requestMidi(
  permissionDesc: MidiPermissionDescriptor,
): Promise<MidiPermissionResult> {
  const { sysex, software } = permissionDesc;
  return navigator?.requestMIDIAccess({ sysex, software }).then(
    (access) => ({ access, state: PermissionStateEnum.GRANTED }),
    (err) => {
      if (err.name === 'SecurityError') {
        return { state: PermissionStateEnum.DENIED, access: false };
      }
      throw err;
    },
  );
}

function requestCamera(
  permissionDesc: CameraPermissionDescriptor,
): Promise<CameraPermissionResult> {
  return new Promise((resolve, reject) => {
    const { constraints, peerIdentity } = permissionDesc;
    navigator?.mediaDevices?.getUserMedia(
      {
        peerIdentity,
        video: constraints ? constraints : true,
      },
      // @ts-ignore
      (stream) => resolve({ stream, state: PermissionStateEnum.GRANTED }),
      // @ts-ignore
      (err) => {
        if (err.name === 'PermissionDeniedError') {
          resolve({ state: PermissionStateEnum.DENIED });
        }
        reject(err);
      },
    );
  });
}

function requestMicrophone(
  permissionDesc: MicrophonePermissionDescriptor,
): Promise<MicrophonePermissionResult> {
  return new Promise((resolve, reject) => {
    const { constraints, peerIdentity } = permissionDesc;
    navigator?.mediaDevices?.getUserMedia(
      {
        peerIdentity,
        audio: constraints ? constraints : true,
      },
      // @ts-ignore
      (stream) => resolve({ stream, state: PermissionStateEnum.GRANTED }),
      // @ts-ignore
      (err) => {
        if (err.name === 'PermissionDeniedError') {
          resolve({ state: PermissionStateEnum.DENIED });
        }
        reject(err);
      },
    );
  });
}

function requestSpeaker(): Promise<SpeakerPermissionResult> {
  return Promise.reject(new TypeError("speaker can't be requested (yet?)"));
}

function requestDeviceInfo(): Promise<DeviceInfoPermissionResult> {
  return Promise.reject(new TypeError("device-info can't be requested (yet?)"));
}

function requestBackgroundSync(
  permissionDesc: BackgroundSyncPermissionDescriptor,
): Promise<BackgroundSyncPermissionResult> {
  try {
    const { serviceWorker, tag } = permissionDesc;
    return serviceWorker.sync.register(tag).then(
      () => ({ state: PermissionStateEnum.GRANTED }),
      (err: DOMException) => {
        if (err.name === 'NotAllowedError') {
          return { state: PermissionStateEnum.DENIED };
        }
        throw err;
      },
    );
  } catch (e) {
    return Promise.reject(e);
  }
}

function requestBluetooth(
  permissionDesc: BluetoothPermissionDescriptor,
): Promise<BluetoothPermissionResult> {
  if (navigator?.bluetooth && navigator?.bluetooth?.requestDevice) {
    return (
      navigator.bluetooth.requestDevice(
        permissionDesc,
      ) as Promise<BluetoothDevice>
    ).then<BluetoothPermissionResult>(
      (device) => {
        return { state: PermissionStateEnum.PROMPT, devices: [device] };
      },
      (err) => {
        throw err;
      },
    );
  }
  return Promise.reject(new TypeError('navigator.bluetooth not supported'));
}

function requestPersistentStorage(): Promise<PersistentStoragePermissionResult> {
  return navigator?.storage?.persist().then((persisted) => {
    if (persisted) {
      return { state: PermissionStateEnum.GRANTED };
    }
    return { state: PermissionStateEnum.DENIED };
  });
}

type PermissionDescType =
  | GeolocationPermissionDescriptor
  | NotificationsPermissionDescriptor
  | PushPermissionDescriptor
  | MidiPermissionDescriptor
  | CameraPermissionDescriptor
  | MicrophonePermissionDescriptor
  | SpeakerPermissionDescriptor
  | DeviceInfoPermissionDescriptor
  | BackgroundSyncPermissionDescriptor
  | BluetoothPermissionDescriptor
  | PersistentStoragePermissionDescriptor
  | PermissionDescriptor
  | { name: string };

export function request(
  permissionDesc: PermissionDescType,
): Promise<PermissionStatus> {
  switch (permissionDesc.name) {
    case 'geolocation':
      return requestGeolocation(
        permissionDesc as GeolocationPermissionDescriptor,
      );
    case 'notifications':
      return requestNotifications();
    case 'push':
      return requestPush(permissionDesc as PushPermissionDescriptor);
    case 'midi':
      return requestMidi(permissionDesc as MidiPermissionDescriptor);
    case 'camera':
      return requestCamera(permissionDesc as CameraPermissionDescriptor);
    case 'microphone':
      return requestMicrophone(
        permissionDesc as MicrophonePermissionDescriptor,
      );
    case 'speaker':
      return requestSpeaker();
    case 'device-info':
      return requestDeviceInfo();
    case 'background-sync':
      return requestBackgroundSync(
        permissionDesc as BackgroundSyncPermissionDescriptor,
      );
    case 'bluetooth':
      return requestBluetooth(permissionDesc as BluetoothPermissionDescriptor);
    case 'persistent-storage':
      return requestPersistentStorage();
    default:
      // Issue: Permissions doesn't specify what to do for an unrecognized
      // PermissionName.
      return Promise.reject(
        new TypeError('Unknown PermissionName: ' + permissionDesc?.name),
      );
  }
}

export function polyfill() {
  if (!window.navigator.permissions) {
    // @ts-ignore
    window.navigator.permissions = {} as any;
  }
  // @ts-ignore
  if (!window.navigator.permissions.request) {
    // @ts-ignore
    window.navigator.permissions.request = request;
  }
  if (!window.navigator.permissions.query) {
    // @ts-ignore
    window.navigator.permissions.query = ({ name }) => {
      switch (name) {
        case 'geolocation':
          return queryGeolocation();
        default:
          return Promise.resolve({ state: PermissionStateEnum.DENIED });
      }
    };
  }
}
